import { useAppStore } from '@/state/store';
import { ProfileGraph } from './ProfileGraph';

export function ProfileInspector() {
  const model = useAppStore((s) => s.model);

  if (!model) {
    return (
      <div className="empty-state">
        <div className="empty-state-title">No profile loaded</div>
        <div className="empty-state-subtitle">Import a .C file to inspect its raw and trimmed profile.</div>
      </div>
    );
  }

  const { raw, trim, normalized } = model;

  return (
    <div className="panel-scroll">
      <h2 className="section-title">Profile Inspector</h2>
      <p className="section-subtitle">
        {raw.diagnostics.parsedRows} points parsed · {trim.leadIn.length + trim.leadOut.length} lead-in/out
        points removed · {trim.trimmedPoints.length} points used for the 3D model. Scroll to zoom, drag to pan.
      </p>

      <ProfileGraph
        allPoints={raw.points}
        leadIn={trim.leadIn}
        trimmed={trim.trimmedPoints}
        leadOut={trim.leadOut}
        gaps={trim.gaps}
        unit={normalized.unit}
      />

      {(() => {
        const filled = trim.gaps.filter((g) => g.filled);
        const unresolved = trim.gaps.filter((g) => !g.filled);
        return (
          <>
            {filled.length > 0 && (
              <div
                style={{
                  marginTop: 16,
                  padding: '12px 14px',
                  background: 'var(--steel-wash)',
                  border: '1px solid var(--steel)',
                  borderRadius: 8,
                  fontSize: 12,
                  color: 'var(--text-primary)',
                  lineHeight: 1.5,
                }}
              >
                <strong style={{ color: 'var(--steel)' }}>
                  {filled.length} region{filled.length === 1 ? '' : 's'} reconstructed from
                  {model.detailFileName ? ` "${model.detailFileName}"` : ' a companion file'}
                </strong>
                <div style={{ marginTop: 6, color: 'var(--text-secondary)' }}>
                  {filled.map((g, i) => (
                    <div key={i}>
                      Position {g.fromPosition.toFixed(1)}–{g.toPosition.toFixed(1)} {normalized.unit} — this segment
                      is an estimate rescaled to match the real data on both sides, not verified machine data. Shown
                      dashed in steel blue on the graph.
                    </div>
                  ))}
                </div>
              </div>
            )}

            {unresolved.length > 0 && (
              <div
                style={{
                  marginTop: 16,
                  padding: '12px 14px',
                  background: 'var(--danger-wash)',
                  border: '1px solid var(--danger)',
                  borderRadius: 8,
                  fontSize: 12,
                  color: 'var(--text-primary)',
                  lineHeight: 1.5,
                }}
              >
                <strong style={{ color: 'var(--danger)' }}>
                  {unresolved.length} possible missing-geometry {unresolved.length === 1 ? 'region' : 'regions'}{' '}
                  detected
                </strong>
                <div style={{ marginTop: 6, color: 'var(--text-secondary)' }}>
                  {unresolved.map((g, i) => (
                    <div key={i}>
                      Position {g.fromPosition.toFixed(1)}–{g.toPosition.toFixed(1)} {normalized.unit} — radius jumps
                      from {g.fromRadius.toFixed(2)} to {g.toRadius.toFixed(2)} {normalized.unit} with nothing
                      recorded in between. This is likely shaped by a separate operation (knob forming, sanding, or
                      cup routing) not included in this file. Upload a companion detail file in the sidebar (e.g. a
                      .V or .U file) if one has data in this region.
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        );
      })()}

      <div style={{ marginTop: 24 }}>
        <h3 style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>
          Import Diagnostics
        </h3>
        <div className="stat-list" style={{ maxWidth: 480 }}>
          <div className="stat-row">
            <span className="stat-label">Delimiter detected</span>
            <span className="stat-value">{raw.diagnostics.delimiter}</span>
          </div>
          <div className="stat-row">
            <span className="stat-label">Decimal style</span>
            <span className="stat-value">{raw.diagnostics.decimalStyle}</span>
          </div>
          <div className="stat-row">
            <span className="stat-label">Total lines in file</span>
            <span className="stat-value">{raw.diagnostics.totalLines}</span>
          </div>
          <div className="stat-row">
            <span className="stat-label">Skipped invalid rows</span>
            <span className="stat-value">{raw.diagnostics.skippedRows}</span>
          </div>
          <div className="stat-row">
            <span className="stat-label">Auto-trim confidence</span>
            <span className="stat-value">{(trim.confidence * 100).toFixed(0)}%</span>
          </div>
          <div className="stat-row">
            <span className="stat-label">Reconstructed regions</span>
            <span className="stat-value">{trim.gaps.filter((g) => g.filled).length}</span>
          </div>
          <div className="stat-row">
            <span className="stat-label">Unresolved missing-geometry regions</span>
            <span className="stat-value">{trim.gaps.filter((g) => !g.filled).length}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
